import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    ActionButton,
    AlertDialog,
    AlertDialogContent,
    Badge,
    Button,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Input,
    Select,
    TextArea,
    Tooltip,
} from "@jhool-io/fe-components";
import { parseISO } from "date-fns";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormatOptionLabelMeta, SelectInstance } from "react-select";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ReactDOM from "react-dom";
import {
    IClaimAdjustments,
    IInsurancePayments,
} from "../../../types/remits.types";

import ConfirmPaymentIcon from "../../../../../components/Icons/ConfirmPayment";
import { APP_COLORS } from "../../../../../utils/constants";
import useToast from "../../../../../hooks/useToast";
import { usePostPayments } from "../../../hooks/remits.mutations";
import {
    useUnMatchPayment,
    useUpdatePayment,
} from "../../../../../hooks/mutations/billing";
import { IUpdatePayment } from "../../../../../utils/types/billing";
import {
    addPracticeParamToUrl,
    buildSuccessMessage,
    displayNameInRightFormat,
    formatDate,
    handleFormatDatePickerValue,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../../utils/helpers";
import {
    WriteOffCodes,
    WriteOffCodesDecription,
    WriteOffSource,
} from "../../../../billing/types/billing.types";
import { formatRemarkOptionLabel } from "../../../../billing/helpers/billing.helpers";
import MinusCircleIcon from "../../../../../components/Icons/MinusCircle";
import { useGetRemarkCodes } from "../../../../remark-codes/hooks/remark-code.queries";
import InfoIcon from "../../../../../icons/Info";
import AlertTraingleIcon from "../../../../../icons/AlertTraingle";
import EditIcon from "../../../../../icons/Edit";
import DeleteIcon from "../../../../../icons/Delete";
import { useAppSelector } from "../../../../../hooks/useRedux";
import { getPaymentRemarkCodeTotals } from "../../../helpers/helpers.remits";

interface InsurancePaymentItemProps {
    payment: IInsurancePayments;
    remitClaimAdjustments: IClaimAdjustments[];
    remittanceId: string;
    invoiceId: string;
    claimReceivedDate?: string;
    remarkCodePaymentTotals?: Map<string, number>;
    adjustmentCodeTotals?: Map<string, number>;
}

type Dialogs = "delete_payment" | "post_payment" | "edit_warning";

type Options = {
    label: React.ReactNode;
    value: string | undefined;
};

type RemarkCodePair = {
    code: string;
    amount: number | null;
};

export default function InsurancePaymentItem({
    payment,
    remitClaimAdjustments,
    remittanceId,
    invoiceId,
    claimReceivedDate,
    remarkCodePaymentTotals = new Map(),
    adjustmentCodeTotals = new Map(),
}: InsurancePaymentItemProps) {
    const getDefaultPaymentSource = () => {
        if (payment.payment_type === "ip" || payment.payment_type === "rip") {
            return "interest_payment";
        }

        return "insurance";
    };
    const [isEditing, setIsEditing] = React.useState(false);
    const codeSelectRefs = React.useRef<(SelectInstance | null)[]>([]);
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [paymentSource, setPaymentSource] = React.useState<string>(() =>
        getDefaultPaymentSource()
    );
    const [rcValidationError, setRCValidationError] = React.useState<
        string | null
    >(null);
    const [localDateOfPayment, setLocalDateOfPayment] =
        React.useState<Date | null>(
            payment.payment_date
                ? parseISO(
                      payment.payment_date
                          .replace("+00:00", "")
                          .replace("Z", "")
                  )
                : null
        );
    const [searchParams, setSearchParams] = useSearchParams();

    const updatePayment = useUpdatePayment();

    const { claimId, noteId, clientId } = useParams();

    const { practice } = useAppSelector((state) => state.userPractice);

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const postPayments = usePostPayments();

    const deletePayment = useUnMatchPayment();

    const remarkCode = useGetRemarkCodes();

    const getRemarkCodesToDisplay = () => {
        const newRemarkCodes = payment.remark_codes_payment;

        if (newRemarkCodes) return newRemarkCodes;

        if (
            !payment.remark_codes ||
            (payment.remark_codes && payment.remark_codes.length === 0)
        ) {
            return null;
        }

        return Object.fromEntries(
            payment.remark_codes.map((key) => [key, null])
        );
    };

    const remarkCodes = getRemarkCodesToDisplay();

    const {
        register,
        control,
        reset,
        setValue,
        watch,
        handleSubmit,
        formState: { errors, dirtyFields },
    } = useForm<IUpdatePayment>({
        mode: "onSubmit",
        resolver: yupResolver(
            yup.object().shape({
                remark_codes_payment_form: yup.array().of(
                    yup.object().shape({
                        amount: yup
                            .mixed()
                            .test(
                                "is-amount-valid",
                                "Enter valid amount",
                                (value) => {
                                    if (
                                        value !== undefined &&
                                        value !== null &&
                                        value !== ""
                                    ) {
                                        return yup
                                            .number()
                                            .required("Enter valid amount")
                                            .isValidSync(value);
                                    }

                                    return true;
                                }
                            ),
                        code: yup
                            .string()
                            .when("amount", (amount: string, field) =>
                                amount
                                    ? field.required("Code is required")
                                    : field
                            ),
                    })
                ),
                amount: yup.number().min(0),
                payment_date: yup.string(),
                writeoff_code: yup.string(),
                payment_notes: yup.string(),
            })
        ),
    });

    const paymentType = watch("payment_type") || "";

    const { fields, append, remove } = useFieldArray({
        control,
        name: "remark_codes_payment_form",
    });

    const paymentRemarkCodeTotals = getPaymentRemarkCodeTotals([payment]);
    const editedRemarkCodeTotals = new Map<string, number>();
    (watch("remark_codes_payment_form") ?? []).forEach((pair) => {
        if (!pair.code) return;

        const code = pair.code.toLowerCase();
        editedRemarkCodeTotals.set(
            code,
            (editedRemarkCodeTotals.get(code) ?? 0) + Number(pair.amount ?? 0)
        );
    });
    const remarkCodeWarning = (codeValue: string | undefined) => {
        if (!codeValue) return null;

        const code = codeValue.toLowerCase();
        const expected = adjustmentCodeTotals.get(code);
        if (expected == null) return null;

        const total = isEditing
            ? (remarkCodePaymentTotals.get(code) ?? 0) -
              (paymentRemarkCodeTotals.get(code) ?? 0) +
              (editedRemarkCodeTotals.get(code) ?? 0)
            : remarkCodePaymentTotals.get(code) ?? 0;

        return Math.round(total * 100) !== Math.round(expected * 100)
            ? `${codeValue.toUpperCase()} total should be ${showMoneyInAppFormat(
                  expected
              )}`
            : null;
    };

    const writeOffCodesForSelect: Options[] = Object.values(WriteOffCodes).map(
        (item) => ({
            value: item,
            label: WriteOffCodesDecription[item],
        })
    );

    const paymentSourcesForSelect: Options[] = Object.values(WriteOffSource)
        .filter(
            (item) =>
                item !== WriteOffSource.CO_INSURANCE &&
                item !== WriteOffSource.PR100
        )
        .map((source) => ({
            value: source,
            label: makeStringFirstLetterCapital(removeEnumUnderscore(source)),
        }));

    const remarkCodeForSelect: Options[] = remarkCode.data
        ? remarkCode.data?.data
              .map((item) => ({
                  value: item.code,
                  label: (
                      <span className="capitalize">{`${item.code}: ${item.description}`}</span>
                  ),
              }))
              .sort((a, b) => a.value.localeCompare(b.value))
        : [];

    const getWriteOffCodeDescription = (code: string) => {
        const writeOffDescriptions = Object.values(WriteOffCodesDecription);
        const matchingDescription = writeOffDescriptions?.find(
            (description) =>
                code?.toLowerCase() === description.split(":")[0]?.toLowerCase()
        );
        return matchingDescription || "--";
    };

    const isRefundPayment = () => {
        const isRefund = payment.payment_type === "ri";

        return isRefund
            ? `(${showMoneyInAppFormat(payment?.amount)})`
            : `${showMoneyInAppFormat(payment?.amount)}`;
    };

    const defaultPaymentTypeValue = () => {
        switch (payment.payment_type) {
            case "wbi":
                return "write_off";

            case "i":
            case "ip":
                return "credit";

            default:
                return "refund";
        }
    };

    const handleEditButtonClicked = () => {
        setValue("amount", payment.amount);
        setValue("payment_notes", payment.payment_notes || "");
        setValue("payment_type", defaultPaymentTypeValue());
        setValue(
            "remark_codes_payment_form",
            remarkCodes
                ? Object.entries(remarkCodes).map(([code, amount]) => ({
                      code,
                      amount: amount === null ? "" : `${amount}`,
                  }))
                : [{ amount: "", code: "" }]
        );
        setValue("writeoff_code", payment.writeoff_code || "");
        setValue("payment_date", payment.payment_date || "");
        setLocalDateOfPayment(
            parseISO(
                payment.payment_date.replace("+00:00", "").replace("Z", "")
            )
        );
        setIsEditing(() => true);
    };

    const handlePaymentCheck = (paymentId: string) => {
        const selected = searchParams.getAll("selectedPayments");

        const updated = selected.includes(paymentId)
            ? selected.filter((id) => id !== paymentId)
            : selected.concat(paymentId);

        searchParams.delete("selectedPayments");
        updated.forEach((id) => searchParams.append("selectedPayments", id));
        setSearchParams(searchParams);
    };

    const getStatusTag = () => {
        switch (payment.payment_status) {
            case "completed":
                return (
                    <Badge
                        content={makeStringFirstLetterCapital(
                            payment.payment_status
                        )}
                        type="success"
                    />
                );
            case "failed":
                return (
                    <Badge
                        content={makeStringFirstLetterCapital(
                            payment.payment_status
                        )}
                        type="danger"
                    />
                );
            default:
                return (
                    <Badge
                        content={makeStringFirstLetterCapital(
                            payment.payment_status
                        )}
                        type="warning"
                    />
                );
        }
    };

    const getPaymentTypeTag = React.useCallback(
        (
            type: string,
            method: string,
            info?: {
                refund_reason?: string;
                cheque_number?: string;
                authorization_number?: string;
            },
            writeoff_code?: string,
            auth_code?: string
        ) => {
            switch (type) {
                case "wbi":
                    return (
                        <div className="flex items-center gap-x-1 whitespace-nowrap">
                            <Badge
                                type="neutral"
                                content={
                                    <>
                                        Write-off:{" "}
                                        <span className="uppercase">
                                            {writeoff_code}
                                        </span>
                                    </>
                                }
                            />

                            <ActionButton
                                variant="normal"
                                size="auto"
                                actionTitle={getWriteOffCodeDescription(
                                    writeoff_code || "--"
                                )}
                                className="uppercase"
                            >
                                <InfoIcon />
                            </ActionButton>
                        </div>
                    );
                case "ri":
                case "rip":
                case "rpr100":
                    return (
                        <span>
                            <Badge
                                type="success"
                                content={
                                    <>
                                        {type === "ri"
                                            ? "Recoupment"
                                            : "Refund"}
                                        :{" "}
                                        {info?.refund_reason
                                            ? removeEnumUnderscore(
                                                  info?.refund_reason
                                              )
                                            : "Others"}
                                    </>
                                }
                            />
                        </span>
                    );
                case "i":
                case "ci":
                case "ip":
                case "pr100":
                    return (
                        <div className="flex items-center gap-x-1 whitespace-nowrap">
                            <Badge
                                type="warning"
                                content={
                                    <>
                                        Credit:{" "}
                                        {removeEnumUnderscore(
                                            method === "cheque"
                                                ? "check"
                                                : method
                                        )}
                                    </>
                                }
                            />
                            {info?.cheque_number && (
                                <ActionButton
                                    variant="normal"
                                    size="auto"
                                    actionTitle={`CHECK NUMBER - ${info.cheque_number}`}
                                    className="uppercase"
                                >
                                    <InfoIcon />
                                </ActionButton>
                            )}
                            {auth_code && (
                                <ActionButton
                                    variant="normal"
                                    size="auto"
                                    actionTitle={`AUTHORIZATION CODE - ${auth_code}`}
                                    className="uppercase"
                                >
                                    <InfoIcon />
                                </ActionButton>
                            )}
                            {info?.authorization_number && (
                                <ActionButton
                                    variant="normal"
                                    size="auto"
                                    actionTitle={`AUTHORIZATION CODE - ${info.authorization_number}`}
                                    className="uppercase"
                                >
                                    <InfoIcon />
                                </ActionButton>
                            )}
                        </div>
                    );
                case "pi":
                    return <Badge type="success" content="Patient insurance" />;

                default:
                    return (
                        <div className="flex items-center gap-x-4 whitespace-nowrap">
                            <Badge type="neutral" content={type ?? "others"} />
                            {info?.refund_reason && (
                                <ActionButton
                                    className="uppercase"
                                    variant="normal"
                                    size="auto"
                                    actionTitle={info.refund_reason}
                                >
                                    <InfoIcon />
                                </ActionButton>
                            )}
                        </div>
                    );
            }
        },
        []
    );

    const getInsurancePaymentSource = (paymentItemType: string) => {
        if (
            paymentItemType === "i" ||
            paymentItemType === "wbi" ||
            paymentItemType === "ri"
        ) {
            return "Insurance";
        }

        return "Interest Payment ";
    };

    const paymentTypeToSend = () => {
        let type;
        if (paymentSource === "insurance") {
            switch (paymentType) {
                case "write_off":
                    type = "wbi";
                    break;
                case "refund":
                    type = "ri";
                    break;
                default:
                    type = "i";
            }
        } else if (paymentSource === "interest_payment") {
            switch (paymentType) {
                case "refund":
                    type = "rip";
                    break;
                default:
                    type = "ip";
            }
        }

        return type;
    };

    const validateRemarkCodesPair = () => {
        const pairs = watch("remark_codes_payment_form") || [];
        const validCodesAndAmounts = remitClaimAdjustments
            ? remitClaimAdjustments.flatMap((item) => [
                  {
                      code: `${item.group_code}${item.reason_code}`.toLowerCase(),
                      amount: item.amount,
                  },
                  ...(item.reason_code_2
                      ? [
                            {
                                code: `${item.group_code}${item.reason_code_2}`.toLowerCase(),
                                amount: item.amount_2,
                            },
                        ]
                      : []),
              ])
            : [];

        // Validate each item
        const validPairs: RemarkCodePair[] = [];

        let errorMessage = "";

        const transformedPair = pairs.map((item) => ({
            code: item.code,
            amount: item.amount ? parseFloat(item.amount) : null,
        }));

        transformedPair.forEach((item) => {
            if (
                validCodesAndAmounts.find(
                    (innerItem) => innerItem.code === item.code
                )
            ) {
                validPairs.push(item);
                const correctAmountForCode = validCodesAndAmounts.find(
                    (innerItem) => innerItem.code === item.code
                )?.amount;

                errorMessage +=
                    correctAmountForCode !== item.amount
                        ? `${item.code.toUpperCase()} - entered ${showMoneyInAppFormat(
                              item.amount ?? 0
                          )}, should be ${showMoneyInAppFormat(
                              correctAmountForCode ?? 0
                          )}\n`
                        : "";

                setRCValidationError(() =>
                    errorMessage.trim() ? errorMessage : null
                );
            }
        });

        if (errorMessage) {
            setDialogInView(() =>
                errorMessage.trim() ? "edit_warning" : null
            );
            return false;
        }
        return true;
    };

    const handleDeletePayment = (id: string) => {
        const deletePaymentPayload = {
            matched_payments: {
                [invoiceId]: [id],
            },
        };
        deletePayment.mutate(deletePaymentPayload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries([
                    "remit-claims",
                    {
                        note_id: noteId,
                    },
                ]);

                queryClient.invalidateQueries({
                    queryKey: [
                        "get-claim",
                        handleRemoveEmptyParamFromQueryParams({
                            claim_id: claimId,
                        }),
                    ],
                });

                queryClient.invalidateQueries({
                    queryKey: ["get-payment"],
                });

                setDialogInView(null);
                toast({
                    mode: "success",
                    message: `${buildSuccessMessage(
                        response.data.successful_payments,
                        response.data.failed_payments
                    )}`,
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete payment at this time",
                });
            },
        });
    };

    const handlePostPayment = (id: string) => {
        postPayments.mutate(
            { payment_ids: [id] },
            {
                onSuccess: (res) => {
                    queryClient.invalidateQueries([
                        "remit-claims",
                        {
                            note_id: noteId,
                        },
                    ]);
                    queryClient.invalidateQueries({
                        queryKey: ["get-payment"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `session-note`, noteId],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Payment(s) posted successfully.",
                    });
                    setDialogInView(null);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not post payment(s).",
                    });
                },
            }
        );
    };

    const handleUpdatePayment = async (data: IUpdatePayment) => {
        const payload: IUpdatePayment = {
            amount: Number.isFinite(data.amount)
                ? Number(data.amount)
                : undefined,
            payment_notes: data.payment_notes,
            payment_date: handleFormatDatePickerValue(data.payment_date),
            remark_codes: data.remark_codes,
            writeoff_code: data.writeoff_code || undefined,
            payment_id: payment?.payment_id || "",
            payment_type: paymentTypeToSend(),
            payment_method: payment?.payment_method || undefined,
            remittance_id: payment?.remittance_id || undefined,
            claim_id: payment?.claim_id || undefined,
            payment_info:
                payment?.payment_info?.cheque_number || payment?.cheque_number
                    ? {
                          cheque_number:
                              payment?.payment_info?.cheque_number ||
                              payment?.cheque_number ||
                              undefined,
                      }
                    : undefined,
        };

        if (dirtyFields.remark_codes_payment_form) {
            const getPairs = data.remark_codes_payment_form?.map((pair) => ({
                code: pair.code,
                amount: pair.amount ? parseFloat(pair.amount) : null,
            }));

            payload.remark_codes_payment =
                getPairs && getPairs?.length > 0
                    ? getPairs?.reduce((acc, curr) => {
                          // eslint-disable-next-line no-param-reassign
                          if (curr.code) acc[curr.code] = curr.amount;
                          return acc;
                      }, {} as Record<string, number | null>)
                    : undefined;
        }

        // clear // remark_codes_payment_form used in form
        payload.remark_codes_payment_form = undefined;

        updatePayment.mutate(payload, {
            onSuccess: async () => {
                toast({
                    mode: "success",
                    message: "Payment updated successfully.",
                });

                if (payment?.payment_status === "pending") {
                    await new Promise<void>((resolve, reject) => {
                        postPayments.mutate(
                            { payment_ids: [payload.payment_id] },
                            {
                                onSuccess: () => {
                                    toast({
                                        mode: "success",
                                        message:
                                            "Payment updated and confirmed successfully.",
                                    });
                                    resolve();
                                },
                                onError: (error) => {
                                    toast({
                                        mode: "error",
                                        message:
                                            error.response?.data.message ??
                                            "Could not confirm payment at this time",
                                    });
                                    reject(error);
                                },
                            }
                        );
                    });
                }

                queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });
                queryClient.invalidateQueries({
                    queryKey: ["get-payment"],
                });

                queryClient.invalidateQueries({
                    queryKey: ["invoices"],
                });
                queryClient.invalidateQueries({
                    queryKey: [
                        "remit-claims",
                        handleRemoveEmptyParamFromQueryParams({
                            note_id: noteId,
                        }),
                    ],
                });
                reset();
                setIsEditing(false);
                setDialogInView(null);
                setRCValidationError(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Error updating payment please try again later",
                });
            },
        });
    };

    return (
        <>
            <Dialog open={dialogInView === "edit_warning"}>
                <DialogContent
                    title="Edit Payment(s)"
                    saveText="Edit payment(s)"
                    variant="center"
                    handleCloseDialog={() => {
                        setDialogInView(null);
                        setRCValidationError(null);
                    }}
                    onSaveClick={() => handleSubmit(handleUpdatePayment)()}
                    isSubmitBtnDisabled={updatePayment.isLoading}
                    isCancelBtnDisabled={updatePayment.isLoading}
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            Are you sure you want to update payment(s)?
                        </h3>
                        <p className="text-base text-danger whitespace-pre-line">
                            {rcValidationError}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
            <AlertDialog open={dialogInView === "delete_payment"}>
                <AlertDialogContent
                    title=" Are you sure you want to Delete this payment?"
                    description={
                        <>
                            <p>
                                Deleting this payment will remove it from your
                                records.
                            </p>

                            <p className="mt-2">
                                You will no longer be able to see this payment.
                            </p>
                        </>
                    }
                    onCancelClick={() => setDialogInView(null)}
                    isDeleting
                    saveText="Delete Payment"
                    onSaveClick={() => handleDeletePayment(payment.payment_id)}
                    isSaveBtnDisabled={deletePayment.isLoading}
                    isCancelBtnDisabled={deletePayment.isLoading}
                />
            </AlertDialog>

            <div className="grid gap-x-3 grid-cols-[20px_150px_150px_150px_150px_300px_100px_120px_260px_150px_100px_150px_200px]  border-b border-b-strokedark [border-bottom:1px_solid_var(--color-stroke)] p-12 text-sm text-nowrap font-normal">
                <div className="flex items-center gap-x-1">
                    {payment.payment_status !== "completed" ||
                    (payment.payment_type !== "wbi" &&
                        payment.payment_status !== "completed") ? (
                        <Checkbox
                            name=""
                            value=""
                            handleChange={() =>
                                handlePaymentCheck(payment.payment_id)
                            }
                            isChecked={searchParams
                                .getAll("selectedPayments")
                                .includes(payment.payment_id)}
                        />
                    ) : null}
                </div>
                <div className="flex items-center gap-x-1 w-[100px]">
                    {!isEditing ? (
                        <>
                            <Tooltip content="delete payment">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    onClick={() => {
                                        setDialogInView("delete_payment");
                                    }}
                                    disabled={postPayments.isLoading}
                                    className="px-4"
                                >
                                    <DeleteIcon />
                                </Button>
                            </Tooltip>
                            <Tooltip content="edit payment">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    onClick={handleEditButtonClicked}
                                    disabled={postPayments.isLoading}
                                    className="px-4"
                                >
                                    <EditIcon />
                                </Button>
                            </Tooltip>
                            {payment.payment_status !== "completed" ||
                            (payment.payment_type !== "wbi" &&
                                payment.payment_status !== "completed") ? (
                                <Tooltip content="confirm payment">
                                    <Button
                                        size="auto"
                                        variant="normal"
                                        disabled={postPayments.isLoading}
                                        onClick={() =>
                                            handlePostPayment(
                                                payment.payment_id
                                            )
                                        }
                                        className="px-4"
                                    >
                                        <ConfirmPaymentIcon />
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <Tooltip content="cancel">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    onClick={() => setIsEditing(false)}
                                    className="px-4"
                                    disabled={updatePayment.isLoading}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="17"
                                        viewBox="0 0 16 17"
                                        fill="none"
                                    >
                                        <path
                                            d="M3.3335 3.83594L12.6668 13.1693M3.3335 13.1693L12.6668 3.83594"
                                            stroke={APP_COLORS.COLOR_DANGER}
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Button>
                            </Tooltip>
                            <Tooltip content="save">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    onClick={() => {
                                        if (validateRemarkCodesPair()) {
                                            handleSubmit(handleUpdatePayment)();
                                        }
                                    }}
                                    className="px-4"
                                    disabled={updatePayment.isLoading}
                                >
                                    <ConfirmPaymentIcon
                                        fill={APP_COLORS.COLOR_PRIMARY}
                                    />
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    {isEditing ? (
                        <div className="w-[120px]">
                            <Controller
                                name="payment_date"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        selected={localDateOfPayment}
                                        onChange={(date) => {
                                            field.onChange(date);
                                            setLocalDateOfPayment(date);
                                        }}
                                        className="h-7 w-[120px]"
                                        popperContainer={({ children }) =>
                                            ReactDOM.createPortal(
                                                children,
                                                document.body
                                            )
                                        }
                                        popperProps={{
                                            strategy: "fixed",
                                            modifiers: [
                                                {
                                                    name: "zIndex",
                                                    options: {
                                                        zIndex: 10,
                                                    },
                                                },
                                            ],
                                        }}
                                    />
                                )}
                            />
                        </div>
                    ) : (
                        formatDate(payment.payment_date, true) || "--"
                    )}
                </div>
                <div className="flex items-center">
                    {claimReceivedDate
                        ? formatDate(claimReceivedDate, true)
                        : "--"}
                </div>
                <div className="flex items-center">
                    {isEditing ? (
                        <Select
                            placeholder="Source"
                            defaultValue={paymentSourcesForSelect.find(
                                (source) => source.value === paymentSource
                            )}
                            onChange={(val) => {
                                setPaymentSource(
                                    (val as Options).value as string
                                );
                                setValue("payment_type", "credit");
                            }}
                            options={paymentSourcesForSelect}
                            formatOptionLabel={(
                                selectData: unknown,
                                formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                            ) => {
                                return formatOptionLabelMeta.context === "menu"
                                    ? (selectData as Options).label
                                    : truncateString(
                                          (selectData as Options)
                                              .label as string,
                                          12
                                      );
                            }}
                            classNames={{
                                container: () => "w-[120px]",
                                control: () => "!h-7 !min-h-7",
                                menu: () => "!w-[130px] !z-[11]",
                                option: () => "!text-[11px]",
                                input: () => "!text-caption !h-5 !p-0 !my-0",
                                menuPortal: () => "!z-[11]",
                            }}
                            isLongListInDialog
                        />
                    ) : (
                        getInsurancePaymentSource(payment.payment_type)
                    )}
                </div>
                <div className="flex items-center">
                    {isEditing && payment.payment_type.includes("wb") ? (
                        <Controller
                            name="writeoff_code"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    placeholder="Select code"
                                    isLongListInDialog
                                    onChange={(val) =>
                                        field.onChange((val as Options).value)
                                    }
                                    options={writeOffCodesForSelect}
                                    defaultValue={writeOffCodesForSelect.filter(
                                        (code) =>
                                            field.value?.includes(
                                                code.value || ""
                                            )
                                    )}
                                    isSearchable
                                    formatOptionLabel={(data, meta) => {
                                        return meta.context === "menu"
                                            ? formatRemarkOptionLabel(
                                                  data,
                                                  meta,
                                                  getWriteOffCodeDescription,
                                                  "writeoff_code"
                                              )
                                            : truncateString(
                                                  (data as Options)
                                                      .label as string,
                                                  10
                                              );
                                    }}
                                    classNames={{
                                        container: () => "w-[120px]",
                                        control: () => "!h-7 !min-h-7",
                                        menu: () => "!w-[200px]",
                                        option: () => "!text-[11px]",
                                        input: () => "!text-xs !h-5 !p-0 !my-0",
                                    }}
                                />
                            )}
                        />
                    ) : (
                        <>
                            {getPaymentTypeTag(
                                payment.payment_type,
                                payment.payment_method,
                                payment.payment_info,
                                payment.writeoff_code,
                                payment.auth_code
                            )}
                        </>
                    )}
                </div>
                <div
                    className={`flex flex-wrap items-center ${
                        payment.payment_type === "ri"
                            ? "text-[--color-text-danger]"
                            : "text-text"
                    }`}
                >
                    {isEditing ? (
                        <Input
                            {...register("amount")}
                            className="h-7 w-[100px]"
                            showCurrency
                        />
                    ) : (
                        isRefundPayment()
                    )}
                </div>
                <div className="flex items-center">
                    <span>{getStatusTag()}</span>
                </div>
                <div>
                    {isEditing ? (
                        <div>
                            {fields.map((fieldItem, i) => {
                                const warning = remarkCodeWarning(
                                    watch(`remark_codes_payment_form.${i}.code`)
                                );

                                return (
                                    <div
                                        key={fieldItem.id}
                                        className="flex items-start mb-2 gap-x-2"
                                    >
                                        <Controller
                                            name={`remark_codes_payment_form.${i}.code`}
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    ref={(
                                                        el: SelectInstance | null
                                                    ) => {
                                                        codeSelectRefs.current[
                                                            i
                                                        ] = el;
                                                    }}
                                                    placeholder="Remark code"
                                                    isSearchable
                                                    options={
                                                        remarkCodeForSelect
                                                    }
                                                    onChange={(val) =>
                                                        field.onChange(
                                                            (val as Options)
                                                                .value
                                                        )
                                                    }
                                                    value={remarkCodeForSelect.find(
                                                        (code) =>
                                                            field.value?.toLowerCase() ===
                                                            code.value?.toLowerCase()
                                                    )}
                                                    formatOptionLabel={(
                                                        selectData: unknown,
                                                        formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                                    ) => {
                                                        return formatOptionLabelMeta.context ===
                                                            "menu"
                                                            ? (
                                                                  selectData as Options
                                                              ).label
                                                            : (
                                                                  (
                                                                      selectData as Options
                                                                  )
                                                                      .value as string
                                                              ).toUpperCase();
                                                    }}
                                                    isLongListInDialog
                                                    classNames={{
                                                        container: () =>
                                                            "w-[120px]",
                                                        control: () =>
                                                            "!h-7 !min-h-7",
                                                        menu: () =>
                                                            "!w-[200px]",
                                                        option: () =>
                                                            "!text-[11px]",
                                                        input: () =>
                                                            "!text-xs !h-5 !p-0 !my-0",
                                                    }}
                                                    hasError={
                                                        !!errors
                                                            ?.remark_codes_payment_form?.[
                                                            i
                                                        ]?.code
                                                    }
                                                    errorText={
                                                        errors
                                                            ?.remark_codes_payment_form?.[
                                                            i
                                                        ]?.code?.message
                                                    }
                                                    errorTextClassName="text-[10px] mt-1 block"
                                                />
                                            )}
                                        />
                                        <div className="relative">
                                            <Input
                                                {...register(
                                                    `remark_codes_payment_form.${i}.amount`
                                                )}
                                                key={`amount-${payment.payment_id}`}
                                                onFocus={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                placeholder="Amount"
                                                hasError={
                                                    !!errors
                                                        ?.remark_codes_payment_form?.[
                                                        i
                                                    ]?.amount
                                                }
                                                errorText={
                                                    errors
                                                        ?.remark_codes_payment_form?.[
                                                        i
                                                    ]?.amount
                                                        ? "Enter valid amount"
                                                        : ""
                                                }
                                                className="h-7 w-[100px] pr-5"
                                                errorTextClassName="-mt-1 text-[10px]"
                                                showCurrency
                                            />
                                            {warning ? (
                                                <ActionButton
                                                    variant="normal"
                                                    size="auto"
                                                    className="absolute right-1 top-[7px] z-[2]"
                                                    actionTitle={warning}
                                                >
                                                    <AlertTraingleIcon className="stroke-[--color-warning-text]" />
                                                </ActionButton>
                                            ) : null}
                                        </div>
                                        <Button
                                            title="Remove pair"
                                            size="auto"
                                            variant="normal"
                                            className={
                                                !!errors
                                                    ?.remark_codes_payment_form?.[
                                                    i
                                                ]?.code ||
                                                !!errors
                                                    ?.remark_codes_payment_form?.[
                                                    i
                                                ]?.amount
                                                    ? "self-start"
                                                    : "self-center"
                                            }
                                            onClick={() => {
                                                remove(i);
                                                const focusIndex =
                                                    i > 0 ? i - 1 : 0;
                                                setTimeout(() => {
                                                    codeSelectRefs.current[
                                                        focusIndex
                                                    ]?.focus();
                                                }, 0);
                                            }}
                                            disabled={fields.length === 1}
                                        >
                                            <MinusCircleIcon />
                                        </Button>
                                    </div>
                                );
                            })}
                            <div className="flex items-center justify-center">
                                <Button
                                    size="auto"
                                    variant="normal"
                                    className="text-xs underline font-medium leading-none"
                                    onClick={() => {
                                        append(
                                            { amount: "", code: "" },
                                            {
                                                shouldFocus: false,
                                            }
                                        );
                                        setTimeout(() => {
                                            codeSelectRefs.current[
                                                fields.length
                                            ]?.focus();
                                        }, 0);
                                    }}
                                >
                                    Add
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-2 flex-wrap">
                            {remarkCodes
                                ? Object.entries(remarkCodes).map(
                                      ([code, amount]) => {
                                          const warning =
                                              remarkCodeWarning(code);

                                          return (
                                              <div
                                                  key={code}
                                                  className="flex items-center gap-x-1"
                                              >
                                                  <Badge
                                                      type="success"
                                                      content={code}
                                                  />
                                                  {warning ? (
                                                      <ActionButton
                                                          variant="normal"
                                                          size="auto"
                                                          actionTitle={warning}
                                                      >
                                                          <AlertTraingleIcon className="stroke-[--color-warning-text]" />
                                                      </ActionButton>
                                                  ) : null}
                                                  <Tooltip
                                                      content={
                                                          amount
                                                              ? showMoneyInAppFormat(
                                                                    amount
                                                                )
                                                              : "--"
                                                      }
                                                  >
                                                      <Button
                                                          variant="normal"
                                                          size="auto"
                                                      >
                                                          <InfoIcon />
                                                      </Button>
                                                  </Tooltip>
                                              </div>
                                          );
                                      }
                                  )
                                : "--"}
                        </div>
                    )}
                </div>
                <div className="flex items-center">
                    {payment.eft_number ? (
                        <Link
                            to={addPracticeParamToUrl(
                                `/remit/EOB/${remittanceId}`,
                                practice?.practice_id
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary text-sm underline"
                        >
                            {payment.eft_number}
                        </Link>
                    ) : (
                        "--"
                    )}
                </div>
                <div className="flex items-center">
                    {formatDate(payment.date_posted) || "--"}
                </div>
                <div className="flex items-center">
                    {Object.keys(payment.posted_by).length > 0
                        ? displayNameInRightFormat({
                              firstName: payment.posted_by.first_name,
                              lastName: payment.posted_by.last_name,
                          })
                        : "Mantle"}
                </div>
                <div className="flex flex-wrap w-[200px] items-center">
                    {isEditing ? (
                        <TextArea
                            {...register("payment_notes")}
                            className="h-[28px] w-[200px] resize-y py-0"
                            placeholder="Notes"
                        />
                    ) : (
                        payment.payment_notes || "--"
                    )}
                </div>
            </div>
        </>
    );
}
